import { offerBillingLabel } from '../../lib/offerBilling';
import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  Surface,
  VerticalStack,
  DataTable,
  DataTableToolbar,
  DataTableFacetedFilter,
  RegistryStatus,
  PaneFooter,
  SectionSeparator,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  cn,
} from '@workspace/silk-ui';
import { useNavigation } from '@/context/NavigationContext';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  Hash,
  MapPin,
  Package,
  Pencil,
  RefreshCw,
  Send,
  User,
  XCircle,
  Zap,
} from 'lucide-react';
import {
  CommerceBreadcrumbHeader,
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
} from '@/views/commerce/catalogHierarchyShared';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { useCommerceRegistryQuery, withCommerceQueryValue } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const QUOTE_SORT_FIELDS = ['customer', 'friendly_quote_id', 'status', 'created_at', 'updated_at'] as const;
import {
  addQuoteNote,
  convertQuote,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  getQuote,
  listQuoteEvents,
  listQuoteNotes,
  listQuotes,
  performQuoteAction,
  updateQuote,
  type CommerceFacetBucket,
  type Quote,
  type QuoteLineItem,
  type QuoteWorkspace,
  type TimelineEvent,
  type TimelineNote,
} from '@/lib/commerce';

// ─── Formatting helpers ───────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  converted: 'Converted',
  accepted: 'Accepted',
  pricing_in_progress: 'Pricing In Progress',
  priced: 'Priced',
  submitted_to_customer: 'Sent to Customer',
  revision_requested: 'Revision Requested',
  requested: 'Requested',
  in_review: 'In Review',
};

function formatStatusLabel(status?: string | null): string {
  if (!status) return '—';
  return STATUS_LABELS[status.toLowerCase()] ?? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const EVENT_LABELS: Record<string, string> = {
  quote_created: 'Quote created',
  quote_submitted: 'Submitted for review',
  quote_approved: 'Quote approved',
  quote_rejected: 'Quote rejected',
  quote_cancelled: 'Quote cancelled',
  quote_converted: 'Converted to order',
  quote_note_added: 'Note added',
  note_added: 'Note added',
  status_changed: 'Status updated',
  workflow_stage_updated: 'Workflow stage updated',
  pricing_updated: 'Pricing updated',
  revision_requested: 'Revision requested',
  customer_updated: 'Customer details updated',
  quote_accepted: 'Accepted by customer',
};

function formatEventTitle(eventType: string): string {
  const key = eventType.toLowerCase().replace(/[.\s]+/g, '_');
  return EVENT_LABELS[key] ?? eventType.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(value?: string | null): string {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatRelativeTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function formatCurrency(value: string | number | null | undefined, currency = 'USD'): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num == null || !Number.isFinite(num)) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(num);
}

function formatMinorCurrency(value: string | number | null | undefined, currency = 'USD'): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num == null || !Number.isFinite(num)) return '—';
  return formatCurrency(num / 100, currency);
}

function formatRoutePricePerMeter(value: number | undefined, currency = 'USD'): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${formatCurrency(value / 1_000_000, currency)}/m`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function positiveQuantity(value: unknown): number {
  const quantity = asFiniteNumber(value);
  return quantity != null && quantity > 0 ? Math.max(1, Math.trunc(quantity)) : 1;
}

type QuoteChargeViewModel = {
  key: string;
  label: string;
  totalAmount: number;
  currency: string;
  cadence: string;
  timing: 'recurring' | 'due_today' | 'usage';
  primary: boolean;
};

type QuoteLineItemViewModel = {
  item: QuoteLineItem;
  quantity: number;
  currency: string;
  unitAmount: number | null;
  extendedAmount: number | null;
  charges: QuoteChargeViewModel[];
  usedSavedPriceFallback: boolean;
};

function quoteChargeTiming(charge: NonNullable<QuoteLineItem['charges']>[number]): QuoteChargeViewModel['timing'] {
  const timing = `${charge.timing ?? ''}`.trim().toLowerCase();
  if (timing === 'recurring') return 'recurring';
  if (timing === 'usage' || `${charge.charge_type ?? ''}`.trim().toLowerCase() === 'usage') return 'usage';
  return 'due_today';
}

function buildQuoteLineItemViewModel(item: QuoteLineItem, fallbackCurrency: string): QuoteLineItemViewModel {
  const quantity = positiveQuantity(item.quantity);
  const currency = `${item.currency ?? fallbackCurrency}`.trim() || fallbackCurrency;
  const rawCharges = Array.isArray(item.charges) ? item.charges : [];
  const primaryCharge = rawCharges.find(charge => ['line_item', 'offer'].includes(`${charge.source_type ?? ''}`.trim().toLowerCase()));
  const savedPrice = asFiniteNumber(item.price);
  const savedDiscountedPrice = asFiniteNumber(item.discounted_price);
  const savedUnitAmount = savedPrice != null && savedPrice > 0
    ? savedPrice
    : savedDiscountedPrice != null && savedDiscountedPrice > 0
      ? savedDiscountedPrice
      : null;
  const primaryUnitAmount = asFiniteNumber(primaryCharge?.amount);
  const usedSavedPriceFallback =
    primaryCharge != null && primaryUnitAmount === 0 && savedUnitAmount != null && savedUnitAmount > 0;
  const legacyPreviewQuantities =
    quantity > 1 && primaryCharge != null && positiveQuantity(primaryCharge.quantity) !== quantity;
  const charges = rawCharges.map((charge, index) => {
    const sourceType = `${charge.source_type ?? ''}`.trim().toLowerCase();
    const primary = charge === primaryCharge;
    const chargeQuantity = legacyPreviewQuantities ? quantity : positiveQuantity(charge.quantity);
    const chargeCurrency = `${charge.currency ?? currency}`.trim() || currency;
    const billingInterval = offerBillingLabel(charge.billing_period_months);
    return {
      key: `${charge.source_id ?? sourceType ?? 'charge'}-${index}`,
      label: `${charge.label ?? ''}`.trim() || formatStatusLabel(charge.charge_type),
      totalAmount: (primary && usedSavedPriceFallback ? savedUnitAmount ?? charge.amount : charge.amount) * chargeQuantity,
      currency: chargeCurrency,
      cadence: billingInterval && billingInterval !== 'one_time' ? `per ${billingInterval}` : formatStatusLabel(charge.charge_type),
      timing: quoteChargeTiming(charge),
      primary,
    };
  });
  const unitAmount = usedSavedPriceFallback ? savedUnitAmount : asFiniteNumber(primaryCharge?.amount ?? item.price);
  const primaryTotal = charges.find(charge => charge.primary)?.totalAmount;
  return {
    item,
    quantity,
    currency,
    unitAmount,
    extendedAmount: primaryTotal ?? (unitAmount == null ? null : unitAmount * quantity),
    charges,
    usedSavedPriceFallback,
  };
}

function buildQuoteChargeTotals(
  lineItems: QuoteLineItemViewModel[],
  pricingSnapshot: Record<string, unknown> | undefined,
): { recurring: number; dueToday: number; usage: number; grandTotal: number } {
  const snapshotTotals = asRecord(pricingSnapshot?.charge_totals);
  const recurring = asFiniteNumber(snapshotTotals?.recurring_total ?? pricingSnapshot?.recurring_total);
  const dueToday = asFiniteNumber(snapshotTotals?.due_today_total ?? pricingSnapshot?.due_today_total);
  const usage = asFiniteNumber(snapshotTotals?.usage_total ?? pricingSnapshot?.usage_total);
  const grandTotal = asFiniteNumber(pricingSnapshot?.grand_total);
  const hasSnapshotTotals = recurring != null || dueToday != null || usage != null || grandTotal != null;
  const hasMeaningfulSnapshotTotal = [recurring, dueToday, usage, grandTotal].some(value => value != null && value !== 0);
  if (hasSnapshotTotals && (!lineItems.some(lineItem => lineItem.usedSavedPriceFallback) || hasMeaningfulSnapshotTotal)) {
    const resolvedRecurring = recurring ?? 0;
    const resolvedDueToday = dueToday ?? 0;
    const resolvedUsage = usage ?? 0;
    return {
      recurring: resolvedRecurring,
      dueToday: resolvedDueToday,
      usage: resolvedUsage,
      grandTotal: grandTotal ?? resolvedRecurring + resolvedDueToday + resolvedUsage,
    };
  }

  const totals = { recurring: 0, dueToday: 0, usage: 0, grandTotal: 0 };
  lineItems.forEach(lineItem => {
    const charges = lineItem.charges.length > 0
      ? lineItem.charges
      : [{
          totalAmount: lineItem.extendedAmount ?? 0,
          timing: `${lineItem.item.timing ?? ''}`.trim().toLowerCase() === 'recurring' || `${lineItem.item.charge_type ?? ''}`.trim().toLowerCase() === 'recurring'
            ? 'recurring' as const
            : 'due_today' as const,
        }];
    charges.forEach(charge => {
      if (charge.timing === 'recurring') totals.recurring += charge.totalAmount;
      else if (charge.timing === 'usage') totals.usage += charge.totalAmount;
      else totals.dueToday += charge.totalAmount;
    });
  });
  totals.grandTotal = totals.recurring + totals.dueToday + totals.usage;
  return totals;
}

function resolveEventNoteId(event: TimelineEvent): string | null {
  const payload = asRecord(event.payload);
  const noteID = payload?.note_id;
  return typeof noteID === 'string' && noteID.trim() ? noteID.trim() : null;
}

function formatJSON(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const primaryButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-bold text-foreground transition-all hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-50';
const destructiveButtonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs font-bold text-destructive transition-all hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50';
const textareaStyles =
  'w-full resize-none rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm text-foreground outline-none transition-all duration-150 focus:border-primary/40 focus:bg-background placeholder:text-muted-foreground/60 disabled:opacity-50';
const inputStyles =
  'w-full rounded-md border border-border/60 bg-muted/20 px-3 py-1.5 text-xs font-bold text-foreground outline-none transition-all focus:border-primary/40 focus:bg-background';

const INITIAL_VISIBLE_NOTES = 8;
const NOTE_LOAD_INCREMENT = 8;

// ─── Static filter options ────────────────────────────────────────────────────

const ALL_QUOTE_STATUS_OPTIONS = [
  'accepted', 'approved', 'cancelled', 'converted', 'draft',
  'in_review', 'pricing_in_progress', 'priced', 'rejected',
  'requested', 'revision_requested', 'submitted', 'submitted_to_customer',
];

// ─── Status helpers ───────────────────────────────────────────────────────────

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'accepted':
    case 'converted':
      return 'success';
    case 'submitted':
    case 'priced':
    case 'submitted_to_customer':
    case 'pricing_in_progress':
    case 'in_review':
    case 'requested':
      return 'warning';
    case 'rejected':
    case 'cancelled':
      return 'error';
    default:
      return 'muted';
  }
}

function normalizeQuoteKind(quote?: Quote | null): string {
  return `${quote?.quote_kind ?? ''}`.trim().toLowerCase();
}

function normalizeWorkflowStage(quote?: Quote | null): string {
  return `${quote?.workflow_stage ?? quote?.status ?? ''}`.trim().toLowerCase();
}

function isConstructionRequestQuote(quote?: Quote | null): boolean {
  return normalizeQuoteKind(quote) === 'construction_request';
}

function readNumericSnapshotField(snapshot: Record<string, unknown> | undefined, key: string): number | null {
  const value = snapshot?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatConstructionDistance(snapshot: Record<string, unknown> | undefined): string {
  const distanceMM = readNumericSnapshotField(snapshot, 'road_distance_mm');
  if (distanceMM == null) return '—';
  const meters = distanceMM / 1000;
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: meters < 100 ? 1 : 0 }).format(meters)} m`;
}

function focusedQuoteIdFromPath(path: string | undefined): string {
  const source =
    typeof path === 'string' && path.trim()
      ? path
      : typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '';
  const search = source.includes('?') ? source.slice(source.indexOf('?')) : '';
  return new URLSearchParams(search).get('quote_id')?.trim() ?? '';
}

function buildQuoteListRoute(workspace: QuoteWorkspace): string {
  return workspace === 'catalog-owner' ? '/commerce/quotes' : '/agent/quotes';
}

function buildQuoteQueryRoute(currentPath: string, workspace: QuoteWorkspace, quoteId: string | null): string {
  const queryIndex = currentPath.indexOf('?');
  const query = queryIndex >= 0 ? currentPath.slice(queryIndex) : '';
  return withCommerceQueryValue(`${buildQuoteListRoute(workspace)}${query}`, 'quote_id', quoteId);
}

// ─── Workflow stepper ─────────────────────────────────────────────────────────

const CONSTRUCTION_STEPS = ['requested', 'pricing_in_progress', 'priced', 'submitted_to_customer', 'accepted', 'converted'] as const;
const STANDARD_STEPS = ['draft', 'submitted', 'approved', 'converted'] as const;

const STEP_LABELS: Record<string, string> = {
  requested: 'Requested',
  pricing_in_progress: 'Pricing',
  priced: 'Priced',
  submitted_to_customer: 'Sent',
  accepted: 'Accepted',
  converted: 'Converted',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
};

function WorkflowStepper({ quote }: { quote: Quote }) {
  const stage = normalizeWorkflowStage(quote);
  const isConstruction = isConstructionRequestQuote(quote);
  const steps = isConstruction ? [...CONSTRUCTION_STEPS] : [...STANDARD_STEPS];
  const isTerminal = stage === 'cancelled' || stage === 'rejected';
  const isRevision = stage === 'revision_requested';
  const effectiveStage = isRevision ? 'requested' : stage;
  const currentIndex = steps.indexOf(effectiveStage as never);

  if (isTerminal) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground/60">
        <XCircle className="h-3.5 w-3.5 text-destructive/60" />
        <span>Quote {stage === 'cancelled' ? 'Cancelled' : 'Rejected'}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center flex-wrap gap-0">
      {steps.map((step, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <div key={step} className="flex items-center gap-0">
            <div
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-[0.06em]',
                isComplete && 'text-emerald-600 dark:text-emerald-400',
                isCurrent && !isRevision && 'bg-primary/10 text-primary',
                isCurrent && isRevision && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
                !isComplete && !isCurrent && 'text-muted-foreground/35',
              )}
            >
              {isComplete && <Check className="h-2.5 w-2.5 flex-shrink-0" />}
              {isCurrent && isRevision && <AlertTriangle className="h-2.5 w-2.5 flex-shrink-0" />}
              {STEP_LABELS[step] ?? step}
            </div>
            {index < steps.length - 1 && (
              <ChevronRight className={cn('h-3 w-3 flex-shrink-0', isComplete ? 'text-emerald-500/50' : 'text-border/50')} />
            )}
          </div>
        );
      })}
      {isRevision && (
        <span className="ml-2 text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
          Revision Requested
        </span>
      )}
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type QuoteTabId = 'overview' | 'items' | 'notes' | 'activity';

// ─── Main component ───────────────────────────────────────────────────────────

export default function QuotesPage({ workspace }: { workspace: QuoteWorkspace }) {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const focusedQuoteId = useMemo(
    () => focusedQuoteIdFromPath((navigation as { path?: string }).path),
    [(navigation as { path?: string }).path],
  );

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [totalQuotes, setTotalQuotes] = useState(0);
  const [statusFacets, setStatusFacets] = useState<CommerceFacetBucket[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [notes, setNotes] = useState<TimelineNote[]>([]);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteFocused, setNoteFocused] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ALL_QUOTE_STATUS_OPTIONS },
    sortFields: QUOTE_SORT_FIELDS,
  });
  const requestSequence = useRef(0);
  const columnFilters = useMemo<ColumnFiltersState>(() =>
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : [],
  [query.facets.status]);
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [activeTab, setActiveTab] = useState<QuoteTabId>('overview');
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [confirmingAction, setConfirmingAction] = useState<string | null>(null);
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(new Set());
  const [visibleNoteCount, setVisibleNoteCount] = useState(INITIAL_VISIBLE_NOTES);
  const [editCustomerData, setEditCustomerData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    formatted_address: '',
  });

  const selectedStatuses = useMemo(() => {
    return query.facets.status;
  }, [query.facets.status]);

  const loadQuoteList = async () => {
    const requestID = ++requestSequence.current;
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const paginationQuery = toLimitOffset(query.pagination);
      const quotePage = await listQuotes(workspace, {
        ...paginationQuery,
        ...(query.search ? { search: query.search } : {}),
        ...(selectedStatuses.length > 0 ? { status: selectedStatuses } : {}),
        ...(query.sort.length > 0 ? { sort: query.sort } : {}),
        include_facets: ['status'],
      });
      if (requestID !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(quotePage.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setQuotes(quotePage.items);
      setTotalQuotes(quotePage.total_count);
      setStatusFacets(quotePage.facets?.status ?? []);
      setSelectedQuoteId(current => {
        if (focusedQuoteId) return focusedQuoteId;
        return current && quotePage.items.some(quote => quote.quote_id === current) ? current : null;
      });
    } catch (err) {
      if (requestID !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load quotes.'));
      }
      setQuotes([]);
      setTotalQuotes(0);
      setSelectedQuoteId(null);
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedQuoteId(focusedQuoteId || null);
  }, [focusedQuoteId]);

  const loadQuoteDetail = async (quoteId: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const [quote, nextNotes, nextEvents] = await Promise.all([
        getQuote(quoteId, workspace),
        listQuoteNotes(quoteId, workspace),
        listQuoteEvents(quoteId, workspace),
      ]);
      setSelectedQuote(quote);
      setNotes(nextNotes);
      setEvents(nextEvents);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load quote details.'));
      }
      setSelectedQuote(null);
      setNotes([]);
      setEvents([]);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (selectedQuote) {
      setEditCustomerData({
        first_name: (selectedQuote.customer_snapshot as { first_name?: string })?.first_name || '',
        last_name: (selectedQuote.customer_snapshot as { last_name?: string })?.last_name || '',
        email: (selectedQuote.customer_snapshot as { email?: string })?.email || '',
        phone: (selectedQuote.customer_snapshot as { phone?: string })?.phone || '',
        formatted_address: (selectedQuote.service_address_snapshot as { formatted_address?: string })?.formatted_address || '',
      });
    }
    setIsEditingCustomer(false);
  }, [selectedQuote]);

  useEffect(() => {
    if (!activeTenant?.id) {
      setQuotes([]);
      setSelectedQuoteId(null);
      setSelectedQuote(null);
      setNotes([]);
      setEvents([]);
      setTotalQuotes(0);
      setLoading(false);
      setDetailLoading(false);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    void loadQuoteList();
  }, [activeTenant?.id, focusedQuoteId, query.pagination.pageIndex, query.pagination.pageSize, query.search, query.sort.join('\u0000'), selectedStatuses.join('\u0000'), workspace]);

  useEffect(() => {
    if (!activeTenant?.id || !selectedQuoteId) {
      setSelectedQuote(null);
      setNotes([]);
      setEvents([]);
      return;
    }
    void loadQuoteDetail(selectedQuoteId);
  }, [activeTenant?.id, selectedQuoteId, workspace]);

  useEffect(() => {
    setConfirmingAction(null);
    setMessage(null);
    setNoteDraft('');
    setNoteFocused(false);
    setExpandedEventIds(new Set());
    setVisibleNoteCount(INITIAL_VISIBLE_NOTES);
  }, [selectedQuoteId]);

  useEffect(() => {
    if (!message) return;
    setToastVisible(true);
    const t = setTimeout(() => setToastVisible(false), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const refreshSelectedQuote = async () => {
    if (!selectedQuoteId) return;
    await Promise.all([loadQuoteList(), loadQuoteDetail(selectedQuoteId)]);
  };

  const handleSelectQuote = useCallback(
    (quoteId: string) => {
      setSelectedQuoteId(quoteId);
      setActiveTab('overview');
      setNoteFocused(false);
      navigation.navigate(buildQuoteQueryRoute(navigation.path, workspace, quoteId));
    },
    [navigation, workspace],
  );

  const handleCloseQuote = useCallback(() => {
    setSelectedQuoteId(null);
    navigation.navigate(buildQuoteQueryRoute(navigation.path, workspace, null), { replace: true });
  }, [navigation, workspace]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmingAction) { setConfirmingAction(null); return; }
        if (selectedQuoteId) { handleCloseQuote(); return; }
      }
      if (!selectedQuoteId || quotes.length === 0) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const currentIndex = quotes.findIndex(q => q.quote_id === selectedQuoteId);
        if (e.key === 'ArrowDown' && currentIndex < quotes.length - 1) {
          e.preventDefault();
          handleSelectQuote(quotes[currentIndex + 1].quote_id);
        }
        if (e.key === 'ArrowUp' && currentIndex > 0) {
          e.preventDefault();
          handleSelectQuote(quotes[currentIndex - 1].quote_id);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedQuoteId, quotes, handleSelectQuote, confirmingAction, handleCloseQuote]);

  const handleQuoteAction = async (action: 'submit' | 'approve' | 'reject' | 'cancel') => {
    if (!selectedQuoteId) return;
    setSubmittingKey(action);
    setError(null);
    setMessage(null);
    setConfirmingAction(null);
    try {
      const updatedQuote = await performQuoteAction(selectedQuoteId, action, undefined, workspace);
      setSelectedQuote(updatedQuote);
      setQuotes(current => current.map(item => (item.quote_id === updatedQuote.quote_id ? updatedQuote : item)));
      const [nextNotes, nextEvents] = await Promise.all([
        listQuoteNotes(selectedQuoteId, workspace),
        listQuoteEvents(selectedQuoteId, workspace),
      ]);
      setNotes(nextNotes);
      setEvents(nextEvents);
      setMessage(`Quote ${formatStatusLabel(action)} successfully.`);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, `Failed to ${action} quote.`));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleConvert = async () => {
    if (!selectedQuoteId) return;
    setSubmittingKey('convert');
    setError(null);
    setMessage(null);
    setConfirmingAction(null);
    try {
      const order = await convertQuote(selectedQuoteId, undefined, workspace);
      await refreshSelectedQuote();
      setMessage(`Converted to order ${(order as { friendly_order_id?: string }).friendly_order_id || order.order_id}.`);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to convert quote.'));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!selectedQuoteId) return;
    setSubmittingKey('update-customer');
    setError(null);
    setMessage(null);
    try {
      const updatedQuote = await updateQuote(
        selectedQuoteId,
        {
          customer_snapshot: {
            first_name: editCustomerData.first_name,
            last_name: editCustomerData.last_name,
            email: editCustomerData.email,
            phone: editCustomerData.phone,
          },
          service_address_snapshot: { formatted_address: editCustomerData.formatted_address },
        },
        workspace,
      );
      setSelectedQuote(updatedQuote);
      setQuotes(current => current.map(item => (item.quote_id === updatedQuote.quote_id ? updatedQuote : item)));
      setIsEditingCustomer(false);
      setMessage('Customer details updated.');
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to update customer details.'));
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleAddNote = async () => {
    const body = noteDraft.trim();
    if (!selectedQuoteId || !body) return;
    setSubmittingKey('note');
    setError(null);
    setMessage(null);
    try {
      const note = await addQuoteNote(selectedQuoteId, body, workspace);
      setNotes(current => [note, ...(Array.isArray(current) ? current : [])]);
      try {
        const nextEvents = await listQuoteEvents(selectedQuoteId, workspace);
        setEvents(nextEvents);
      } catch {
        setEvents(current => [
          {
            event_id: `local-note-${note.note_id}`,
            event_type: 'quote.note.added',
            actor_user_id: note.author_user_id ?? null,
            payload: { note_id: note.note_id },
            created_at: note.created_at,
          },
          ...(Array.isArray(current) ? current : []),
        ]);
      }
      setNoteDraft('');
      setNoteFocused(false);
      setMessage('Note added.');
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to add note.'));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void handleAddNote();
    }
  };

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEventIds(prev => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  };

  const handleOwnerWorkflowTransition = async (nextStage: string) => {
    if (!selectedQuoteId) return;
    setSubmittingKey(`workflow-${nextStage}`);
    setError(null);
    setMessage(null);
    try {
      const updatedQuote = await updateQuote(selectedQuoteId, { workflow_stage: nextStage }, workspace);
      setSelectedQuote(updatedQuote);
      setQuotes(current => current.map(item => (item.quote_id === updatedQuote.quote_id ? updatedQuote : item)));
      setMessage(`Quote moved to ${formatStatusLabel(nextStage)}.`);
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to update quote workflow.'));
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleSellerRevisionRequest = async () => {
    if (!selectedQuoteId) return;
    setSubmittingKey('request-revision');
    setError(null);
    setMessage(null);
    try {
      const updatedQuote = await updateQuote(selectedQuoteId, { workflow_stage: 'revision_requested' }, workspace);
      setSelectedQuote(updatedQuote);
      setQuotes(current => current.map(item => (item.quote_id === updatedQuote.quote_id ? updatedQuote : item)));
      setMessage('Revision requested from catalog owner.');
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to request a quote revision.'));
    } finally {
      setSubmittingKey(null);
    }
  };

  // ─── Table columns ──────────────────────────────────────────────────────────

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
  };

  const columns = useMemo<ColumnDef<Quote>[]>(
    () =>
      [
        {
          id: 'customer',
          accessorFn: quote => [
            quote.customer_snapshot?.first_name,
            quote.customer_snapshot?.last_name,
            quote.customer_snapshot?.email,
          ].filter(Boolean).join(' '),
          header: 'Customer',
          size: 260,
          cell: ({ row }: { row: { original: Quote } }) => {
            const quote = row.original;
            const customerName =
              [quote.customer_snapshot?.first_name, quote.customer_snapshot?.last_name].filter(Boolean).join(' ') ||
              'Unassigned customer';
            const isSelected = selectedQuoteId === quote.quote_id;
            return (
              <button
                type="button"
                onClick={() => handleSelectQuote(quote.quote_id)}
                className="group min-w-0 text-left"
                aria-label={`Open quote ${quote.friendly_quote_id} for ${customerName}`}
              >
                <p className={cn('text-sm font-black transition-colors group-hover:underline', isSelected ? 'text-primary' : 'text-foreground group-hover:text-primary')}>
                  {customerName}
                </p>
                <DescriptionText className="line-clamp-1 text-[11px]">
                  {(quote.customer_snapshot?.email as string) || 'No email provided'}
                </DescriptionText>
              </button>
            );
          },
        },
        {
          accessorKey: 'status',
          header: 'Status',
          size: 160,
          cell: ({ row }: { row: { original: Quote } }) => (
            <RegistryStatus label={formatStatusLabel(row.original.status)} variant={statusVariant(row.original.status)} />
          ),
          filterFn: (row: { getValue: (id: string) => unknown }, id: string, value: unknown) =>
            (value as string[]).includes(row.getValue(id) as string),
        },
        {
          accessorKey: 'friendly_quote_id',
          header: 'Quote',
          size: 140,
          cell: ({ row }: { row: { original: Quote } }) => (
            <span className="font-mono text-[11px] font-bold text-foreground/80">{row.original.friendly_quote_id}</span>
          ),
        },
        {
          accessorKey: 'updated_at',
          header: 'Updated',
          size: 150,
          cell: ({ row }: { row: { original: Quote } }) => (
            <span className="text-[11px] font-bold text-foreground/80">{formatRelativeTime(row.original.updated_at)}</span>
          ),
        },
        {
          id: 'actions',
          enableSorting: false,
          size: 56,
          header: () => null,
          cell: ({ row }: { row: { original: Quote } }) => (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => handleSelectQuote(row.original.quote_id)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
                aria-label={`Open ${row.original.friendly_quote_id}`}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ),
        },
      ],
    [handleSelectQuote, selectedQuoteId],
  );
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: QUOTE_SORT_FIELDS, registry: 'quotes' });

  // ─── Shell view model ───────────────────────────────────────────────────────

  const workspaceTitle = workspace === 'catalog-owner' ? 'Catalog Quotes' : 'Seller Quotes';
  const workspaceDescription =
    workspace === 'catalog-owner'
      ? 'Review quote intake and approval work for the active catalog owner tenant.'
      : 'Review seller-side quotes created in the active sales context and continue collaboration work.';

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'quotes',
    searchQuery: query.search,
    searchPlaceholder: 'Search quotes...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to review quotes.',
    onSelectSection: sectionId =>
      navigation.navigate(
        sectionId === 'quotes' && workspace === 'seller'
          ? '/agent/quotes'
          : sectionId === 'orders' && workspace === 'seller'
            ? '/sales/orders'
            : sectionId === 'carts' && workspace === 'seller'
              ? '/reseller/carts'
              : buildCommerceSectionRoute(sectionId),
      ),
    metrics: [
      { id: 'quote-total', label: 'Total Quotes', value: String(totalQuotes) },
      {
        id: 'quote-stage-focus',
        label: workspace === 'catalog-owner' ? 'Awaiting Pricing' : 'Awaiting Owner',
        value: String(quotes.filter(q => normalizeWorkflowStage(q) === 'requested').length),
      },
    ],
  });

  const sortedNotes = useMemo(
    () =>
      [...(Array.isArray(notes) ? notes : [])].sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime(),
      ),
    [notes],
  );

  const sortedEvents = useMemo(
    () =>
      [...(Array.isArray(events) ? events : [])].sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime(),
      ),
    [events],
  );

  const visibleNotes = useMemo(
    () => sortedNotes.slice(0, visibleNoteCount),
    [sortedNotes, visibleNoteCount],
  );

  const hasMoreNotes = visibleNoteCount < sortedNotes.length;

  const notesById = useMemo(
    () => new Map(sortedNotes.map(note => [note.note_id, note])),
    [sortedNotes],
  );

  // ─── Computed quote state ───────────────────────────────────────────────────

  const quoteStatus = (selectedQuote?.status || '').toLowerCase();
  const quoteWorkflowStage = normalizeWorkflowStage(selectedQuote);
  const quoteKind = normalizeQuoteKind(selectedQuote);
  const isConstructionRequest = isConstructionRequestQuote(selectedQuote);
  const isCatalogOwnerWorkspace = workspace === 'catalog-owner';
  const canSellerEditIntake =
    isConstructionRequest &&
    !isCatalogOwnerWorkspace &&
    (quoteWorkflowStage === 'requested' || quoteWorkflowStage === 'revision_requested');
  const canSellerRequestRevision =
    isConstructionRequest &&
    !isCatalogOwnerWorkspace &&
    (quoteWorkflowStage === 'priced' || quoteWorkflowStage === 'submitted_to_customer');
  const pricingCurrency =
    `${(selectedQuote?.customer_price_snapshot as { currency?: string })?.currency ?? (selectedQuote?.pricing_snapshot as { currency?: string })?.currency ?? 'USD'}`.trim() || 'USD';

  const tabs: { id: QuoteTabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'items', label: 'Services', count: Array.isArray(selectedQuote?.line_items_draft) ? (selectedQuote!.line_items_draft as unknown[]).length : undefined },
    { id: 'notes', label: 'Notes', count: sortedNotes.length || undefined },
    { id: 'activity', label: 'Activity', count: sortedEvents.length || undefined },
  ];

  // ─── Detail panel content ───────────────────────────────────────────────────

  const renderDetailPanel = () => {
    if (!selectedQuote && detailLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="h-8 w-8 text-primary animate-spin" />
        </div>
      );
    }

    if (!selectedQuote) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <XCircle className="h-12 w-12 text-muted-foreground/40 mb-4" />
          <p className="text-sm font-black text-foreground">Quote Not Found</p>
          <DescriptionText className="mt-1">This quote may have been removed or you lack access.</DescriptionText>
          <button type="button" onClick={handleCloseQuote} className="mt-6 text-sm font-bold text-primary hover:underline">
            Back to Queue
          </button>
        </div>
      );
    }

    const routePricingSegments = Array.isArray(selectedQuote.construction_cost_snapshot?.route_segments)
      ? selectedQuote.construction_cost_snapshot.route_segments as Array<{
          segment_id?: string;
          distance_mm?: number;
          policy?: { build_type?: string; currency?: string; price_per_meter_minor_ten_thousandths?: number; minimum_build_cost_minor?: number };
        }>
      : [];
    const calculationStatus = `${selectedQuote.calculation_status ?? ''}`.trim().toLowerCase();
    const calculationFailed = calculationStatus === 'failed';
    const calculationPending = !['calculated', 'failed', 'superseded'].includes(calculationStatus);
    const constructionMode = `${selectedQuote.construction_cost_snapshot?.construction_pricing_mode ?? ''}`.trim();
    const constructionSource = `${selectedQuote.construction_cost_snapshot?.source ?? ''}`.trim();
    const constructionMarkupBps = readNumericSnapshotField(selectedQuote.construction_cost_snapshot, 'customer_markup_bps');
    const overviewContent = (
      <div className="space-y-8 pt-6">
        {/* Customer & Service */}
        <div>
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-muted-foreground/60" />
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/60">Customer & Location</p>
            </div>
            {!isEditingCustomer &&
              quoteStatus !== 'converted' &&
              quoteStatus !== 'cancelled' &&
              (!isConstructionRequest || canSellerEditIntake || isCatalogOwnerWorkspace) && (
                <button
                  type="button"
                  onClick={() => setIsEditingCustomer(true)}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
                  aria-label="Edit customer details"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
          </div>

          {isEditingCustomer ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <VerticalStack gap={1.5}>
                  <label htmlFor="first_name" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">First Name</label>
                  <input id="first_name" className={inputStyles} value={editCustomerData.first_name} onChange={e => setEditCustomerData(prev => ({ ...prev, first_name: e.target.value }))} />
                </VerticalStack>
                <VerticalStack gap={1.5}>
                  <label htmlFor="last_name" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Last Name</label>
                  <input id="last_name" className={inputStyles} value={editCustomerData.last_name} onChange={e => setEditCustomerData(prev => ({ ...prev, last_name: e.target.value }))} />
                </VerticalStack>
                <VerticalStack gap={1.5}>
                  <label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Email</label>
                  <input id="email" type="email" className={inputStyles} value={editCustomerData.email} onChange={e => setEditCustomerData(prev => ({ ...prev, email: e.target.value }))} />
                </VerticalStack>
                <VerticalStack gap={1.5}>
                  <label htmlFor="phone" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Phone</label>
                  <input id="phone" className={inputStyles} value={editCustomerData.phone} onChange={e => setEditCustomerData(prev => ({ ...prev, phone: e.target.value }))} />
                </VerticalStack>
                <div className="sm:col-span-2">
                  <VerticalStack gap={1.5}>
                    <label htmlFor="address" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Service Address</label>
                    <input id="address" className={inputStyles} value={editCustomerData.formatted_address} onChange={e => setEditCustomerData(prev => ({ ...prev, formatted_address: e.target.value }))} />
                  </VerticalStack>
                </div>
              </div>
              <HorizontalStack gap={2} className="justify-end">
                <button type="button" className={secondaryButtonClass} onClick={() => setIsEditingCustomer(false)}>Cancel</button>
                <button type="button" onClick={() => void handleUpdateCustomer()} disabled={submittingKey === 'update-customer'} className={primaryButtonClass}>
                  {submittingKey === 'update-customer' && <RefreshCw className="h-3 w-3 animate-spin" />}
                  Save Changes
                </button>
              </HorizontalStack>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Surface className="p-4 border-border/40">
                <VerticalStack gap={0.5}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-1.5">Contact</p>
                  <p className="text-sm font-bold text-foreground leading-snug">
                    {`${(selectedQuote.customer_snapshot as { first_name?: string })?.first_name || ''} ${(selectedQuote.customer_snapshot as { last_name?: string })?.last_name || ''}`.trim() || '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">{(selectedQuote.customer_snapshot as { email?: string })?.email || '—'}</p>
                  {(selectedQuote.customer_snapshot as { phone?: string })?.phone && (
                    <p className="text-xs text-muted-foreground">{(selectedQuote.customer_snapshot as { phone?: string })?.phone}</p>
                  )}
                </VerticalStack>
              </Surface>
              <Surface className="p-4 border-border/40">
                <div className="flex items-start gap-3">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 mt-0.5" />
                  <VerticalStack gap={0.5}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-1.5">Service Address</p>
                    <p className="text-sm font-bold text-foreground leading-snug">
                      {(selectedQuote.service_address_snapshot as { formatted_address?: string })?.formatted_address || '—'}
                    </p>
                  </VerticalStack>
                </div>
              </Surface>
            </div>
          )}
        </div>

        <SectionSeparator />

        {/* Pricing & Commercials */}
        <div>
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-3.5 w-3.5 text-muted-foreground/60" />
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/60">Pricing & Commercials</p>
            </div>
          </div>

          {isConstructionRequest && !isCatalogOwnerWorkspace && calculationFailed && (
            <Surface className="border border-destructive/30 bg-destructive/5 p-4 mb-4">
              <div className="flex items-start gap-3">
                <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-destructive mb-1">Estimate Failed</p>
                  <DescriptionText className="text-xs">
                    Construction pricing could not be completed. Review quote activity for the recorded failure details and retry guidance.
                  </DescriptionText>
                  <button type="button" className="mt-3 text-xs font-bold text-destructive hover:underline" onClick={() => setActiveTab('activity')}>
                    View activity →
                  </button>
                </div>
              </div>
            </Surface>
          )}

          {isConstructionRequest &&
            !isCatalogOwnerWorkspace &&
            calculationPending && (
              <Surface className="border border-dashed border-warning/30 bg-warning/5 p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black text-warning mb-1">Estimate In Progress</p>
                    <DescriptionText className="text-xs">
                      Submitted {formatRelativeTime(selectedQuote.submitted_at || selectedQuote.created_at)} — pricing completes automatically when the required construction evidence is available.
                    </DescriptionText>
                    {canSellerRequestRevision && (
                      <button type="button" className="mt-3 text-xs font-bold text-warning hover:underline disabled:opacity-50" onClick={() => void handleSellerRevisionRequest()} disabled={submittingKey !== null}>
                        Request a revision →
                      </button>
                    )}
                  </div>
                </div>
              </Surface>
            )}

          <PropertyGrid cols={2}>
            <PropertyItem label="Commercial State" value={formatStatusLabel(selectedQuote.commercial_state) || 'Estimate'} />
            <PropertyItem label="Calculation Status" value={formatStatusLabel(selectedQuote.calculation_status) || '—'} />
            <PropertyItem label="Construction Mode" value={constructionMode ? formatStatusLabel(constructionMode) : (calculationStatus === 'awaiting_construction' ? 'Awaiting estimate' : '—')} />
            <PropertyItem label="Construction Source" value={constructionSource || '—'} />
            <PropertyItem label="Road Distance" value={formatConstructionDistance(selectedQuote.construction_cost_snapshot)} />
            <PropertyItem label="Distance Cost" value={formatMinorCurrency(readNumericSnapshotField(selectedQuote.construction_cost_snapshot, 'distance_cost_amount_minor'), pricingCurrency)} />
            <PropertyItem label="Minimum Build Cost" value={formatMinorCurrency(readNumericSnapshotField(selectedQuote.construction_cost_snapshot, 'effective_minimum_build_cost_amount_minor'), pricingCurrency)} />
            <PropertyItem label="Minimum Applied" value={selectedQuote.construction_cost_snapshot?.minimum_build_cost_applied === true ? 'Yes' : 'No'} />
            <PropertyItem label="Internal Build Cost" value={formatMinorCurrency(readNumericSnapshotField(selectedQuote.construction_cost_snapshot, 'internal_cost_minor'), pricingCurrency)} />
            <PropertyItem label="Customer Markup" value={constructionMarkupBps == null ? '—' : `${constructionMarkupBps / 100}%`} />
            <PropertyItem label="Customer Construction Charge" value={formatMinorCurrency(readNumericSnapshotField(selectedQuote.customer_price_snapshot, 'one_time_construction_amount_minor'), pricingCurrency)} />
            <PropertyItem label="Tax" value={formatMinorCurrency(readNumericSnapshotField(selectedQuote.customer_price_snapshot, 'tax_amount_minor'), pricingCurrency)} />
            <PropertyItem label="Monthly Recurring" value={formatMinorCurrency(readNumericSnapshotField(selectedQuote.customer_price_snapshot, 'monthly_recurring_amount_minor'), pricingCurrency)} />
            <PropertyItem label="Pricing Revision" value={selectedQuote.pricing_revision != null ? String(selectedQuote.pricing_revision) : '0'} />
            <PropertyItem label="Valid Until" value={selectedQuote.valid_until ? formatDate(selectedQuote.valid_until) : 'Not issued'} />
            <PropertyItem label="Origin" value={selectedQuote.quote_origin?.replace(/_/g, ' ') || 'Internal'} />
          </PropertyGrid>
          {routePricingSegments.length > 0 && (
            <div className="mt-5 border-t border-border/40 pt-4">
              <p className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/60">Route rate segments</p>
              <div className="divide-y divide-border/30 border-y border-border/40">
                {routePricingSegments.map((segment, index) => (
                  <div key={segment.segment_id || index} className="grid grid-cols-2 gap-3 py-3 text-xs sm:grid-cols-4">
                    <span className="font-bold text-foreground">{segment.policy?.build_type || 'Default build'}</span>
                    <span className="text-muted-foreground">{formatConstructionDistance({ road_distance_mm: segment.distance_mm })}</span>
                    <span className="text-muted-foreground">{formatRoutePricePerMeter(segment.policy?.price_per_meter_minor_ten_thousandths, segment.policy?.currency || pricingCurrency)}</span>
                    <span className="text-muted-foreground sm:text-right">Min {formatMinorCurrency(segment.policy?.minimum_build_cost_minor, segment.policy?.currency || pricingCurrency)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    const itemsLabel =
      selectedQuote.quote_origin === 'business_signup_request' ||
      (selectedQuote.request_snapshot as { request_kind?: string })?.request_kind === 'business_signup'
        ? 'Interested Services'
        : 'Line Items Draft';
    const lineItems = Array.isArray(selectedQuote.line_items_draft)
      ? selectedQuote.line_items_draft
      : [];
    const lineItemViewModels = lineItems.map(item => buildQuoteLineItemViewModel(item, pricingCurrency));
    const chargeTotals = buildQuoteChargeTotals(lineItemViewModels, selectedQuote.pricing_snapshot);

    const itemsContent = (
      <div className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-3.5 w-3.5 text-muted-foreground/60" />
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/60">{itemsLabel}</p>
          </div>
          <span className="px-2 py-0.5 rounded text-[10px] font-black bg-muted/60 text-muted-foreground/80 border border-border/40">
            {lineItems.length} Items
          </span>
        </div>

        {lineItems.length > 0 ? (
          <div className="rounded-lg border border-border/40 overflow-hidden">
            <div className="divide-y divide-border/30">
              {lineItemViewModels.map((lineItem, index) => {
                const itemizedCharges = lineItem.charges.filter(charge => !charge.primary);
                return (
                  <div key={`${lineItem.item.product_id ?? lineItem.item.variant_id ?? 'item'}-${index}`} className="px-4 py-3 hover:bg-muted/5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <Package className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-foreground truncate">
                            {lineItem.item.product_name || lineItem.item.display_name || lineItem.item.name || 'Unnamed Item'}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            <span className="font-black text-foreground/70">Qty {lineItem.quantity}</span>
                            {lineItem.unitAmount != null && (
                              <span> × {formatMinorCurrency(lineItem.unitAmount, lineItem.currency)} each</span>
                            )}
                          </p>
                          {(lineItem.item.sku || lineItem.item.product_id) && (
                            <p className="font-mono text-[10px] text-muted-foreground">{lineItem.item.sku || lineItem.item.product_id}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        {lineItem.extendedAmount != null ? (
                          <p className="font-mono text-sm font-bold text-foreground">
                            {formatMinorCurrency(lineItem.extendedAmount, lineItem.currency)}
                          </p>
                        ) : (
                          <p className="font-mono text-sm font-bold text-muted-foreground/50">—</p>
                        )}
                        {lineItem.item.billing_period_months && (
                          <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60">
                            {offerBillingLabel(lineItem.item.billing_period_months)}
                          </p>
                        )}
                      </div>
                    </div>
                    {itemizedCharges.length > 0 && (
                      <div className="mt-3 ml-6 border-t border-border/30 pt-2 space-y-1.5">
                        {itemizedCharges.map(charge => (
                          <div key={charge.key} className="flex items-center justify-between gap-4 text-xs">
                            <div className="min-w-0">
                              <p className="font-bold text-foreground/80 truncate">{charge.label}</p>
                              <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60">{charge.cadence}</p>
                            </div>
                            <p className="font-mono font-bold text-foreground shrink-0">
                              {formatMinorCurrency(charge.totalAmount, charge.currency)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3 border-t border-border/40 bg-muted/10 px-4 py-3 sm:grid-cols-4">
              <PropertyItem label="Recurring" value={formatMinorCurrency(chargeTotals.recurring, pricingCurrency)} />
              <PropertyItem label="Due Today" value={formatMinorCurrency(chargeTotals.dueToday, pricingCurrency)} />
              <PropertyItem label="Usage" value={formatMinorCurrency(chargeTotals.usage, pricingCurrency)} />
              <PropertyItem label="Charge Total" value={formatMinorCurrency(chargeTotals.grandTotal, pricingCurrency)} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <Package className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-bold text-foreground">No items yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {selectedQuote.quote_origin === 'business_signup_request' ? 'No interested services captured.' : 'No line items in this draft.'}
            </p>
          </div>
        )}
      </div>
    );

    const notesContent = (
      <div className="pt-6">
        <div className="mb-8">
          <textarea
            value={noteDraft}
            onChange={e => setNoteDraft(e.target.value)}
            onFocus={() => setNoteFocused(true)}
            onBlur={() => { if (!noteDraft.trim()) setNoteFocused(false); }}
            onKeyDown={handleNoteKeyDown}
            placeholder="Add a note..."
            rows={noteFocused ? 4 : 1}
            className={cn(textareaStyles, 'min-h-[38px]', noteFocused && 'min-h-[100px]')}
          />
          {noteFocused && (
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground/60">⌘ Return to submit</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setNoteDraft(''); setNoteFocused(false); }} className={secondaryButtonClass}>Cancel</button>
                <button type="button" onClick={() => void handleAddNote()} disabled={!noteDraft.trim() || submittingKey !== null} className={primaryButtonClass}>
                  {submittingKey === 'note' ? 'Saving...' : 'Add Note'}
                </button>
              </div>
            </div>
          )}
        </div>

        {sortedNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-bold text-foreground">No notes yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Quote notes will appear here as collaborators add context.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between gap-3">
              <DescriptionText className="text-[11px] font-bold text-muted-foreground/70">
                Showing {visibleNotes.length} of {sortedNotes.length} notes
              </DescriptionText>
            </div>
            <div className="max-h-[min(60vh,36rem)] overflow-y-auto pr-2">
              <div className="space-y-0">
                {visibleNotes.map((note, index) => {
                  const isLast = index === visibleNotes.length - 1;
                  const wasUpdated =
                    Boolean(note.updated_at) &&
                    new Date(note.updated_at).getTime() !== new Date(note.created_at || '').getTime();
                  const initial = (note.author_user_id || 'S').charAt(0).toUpperCase();
                  return (
                    <div key={note.note_id} className="relative flex gap-4 pb-6">
                      {!isLast && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border/40" />}
                      <div className="relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                        <span className="text-[9px] font-black text-primary">{initial}</span>
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="mb-1 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <span className="text-xs font-bold text-foreground">{note.author_user_id || 'System'}</span>
                            <p className="text-[10px] text-muted-foreground/60" title={formatDate(note.created_at)}>
                              Created {formatDate(note.created_at)}
                            </p>
                            {wasUpdated ? (
                              <p className="text-[10px] text-muted-foreground/50" title={formatDate(note.updated_at)}>
                                Updated {formatDate(note.updated_at)}
                              </p>
                            ) : null}
                          </div>
                          <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground" title={formatDate(note.created_at)}>
                            {formatRelativeTime(note.created_at)}
                          </span>
                        </div>
                        <div className="rounded-lg border border-border/40 bg-muted/5 px-3.5 py-2.5">
                          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">{note.body}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {hasMoreNotes ? (
              <div className="flex justify-center pt-4">
                <button
                  type="button"
                  onClick={() => setVisibleNoteCount(current => Math.min(current + NOTE_LOAD_INCREMENT, sortedNotes.length))}
                  className={secondaryButtonClass}
                >
                  Load more notes
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    );

    const activityContent = (
      <div className="pt-6">
        {sortedEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-bold text-foreground">No activity yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Workflow events and system history will appear here as the quote progresses.</p>
          </div>
        ) : (
          <div className="space-y-0">
            {sortedEvents.map((event, index) => {
              const isLast = index === sortedEvents.length - 1;
              const isExpanded = expandedEventIds.has(event.event_id);
              const linkedNote = notesById.get(resolveEventNoteId(event) ?? '');
              const payload = asRecord(event.payload);
              const hasPayload = Boolean(payload && Object.keys(payload).length > 0);
              return (
                <div key={event.event_id} className="relative flex gap-4 pb-6">
                  {!isLast && <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border/40" />}
                  <div className="relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted border border-border/60">
                    <Zap className="h-3 w-3 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs font-bold text-foreground">{formatEventTitle(event.event_type || '')}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums" title={formatDate(event.created_at)}>
                        {formatRelativeTime(event.created_at)}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                      {event.actor_user_id && event.actor_user_id !== 'System' ? event.actor_user_id : 'System'}
                    </p>
                    {linkedNote ? (
                      <div className="mt-2 overflow-hidden rounded-lg border border-border/40 bg-muted/10">
                        <div className="flex items-start justify-between gap-3 border-b border-border/25 bg-background/80 px-3.5 py-2">
                          <div className="min-w-0">
                            <p className="text-[9px] font-black uppercase tracking-[0.18em] text-muted-foreground/55">
                              Linked Note
                            </p>
                            <p className="text-[11px] font-bold text-foreground">
                              {linkedNote.author_user_id || 'System'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-muted-foreground">{formatRelativeTime(linkedNote.created_at)}</p>
                            <p className="text-[9px] text-muted-foreground/55" title={formatDate(linkedNote.created_at)}>
                              {formatDate(linkedNote.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="px-3.5 py-3">
                          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                            {linkedNote.body}
                          </p>
                        </div>
                        <div className="px-3.5 pb-3">
                          <button
                            type="button"
                            onClick={() => setActiveTab('notes')}
                            className="text-[10px] font-bold text-primary hover:underline"
                          >
                            Open in Notes
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {hasPayload && (
                      <>
                        {isExpanded && (
                          <div className="mt-2 rounded-md border border-border/30 bg-muted/20 overflow-hidden">
                            <div className="flex items-center justify-between border-b border-border/20 bg-background px-3 py-1.5">
                              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50">payload.json</span>
                              <button type="button" onClick={() => void navigator.clipboard.writeText(formatJSON(event.payload))} className="text-[9px] font-black text-primary hover:underline">Copy</button>
                            </div>
                            <pre className="p-3 text-[10px] font-mono leading-relaxed text-foreground/70 overflow-x-auto max-h-[200px]">
                              {formatJSON(event.payload)}
                            </pre>
                          </div>
                        )}
                        <button type="button" onClick={() => toggleEventExpanded(event.event_id)} className="mt-1.5 text-[9px] font-bold text-primary/60 hover:text-primary transition-colors">
                          {isExpanded ? 'hide payload' : 'show payload'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );

    // ── Sidebar (same structure as OrderView) ────────────────────────────────
    const sidebar = (
      <VerticalStack gap={6}>
        <VerticalStack gap={3}>
          <VerticalStack gap={2}>
            <div className="flex items-center gap-2 flex-wrap">
              <RegistryStatus label={formatStatusLabel(selectedQuote.status)} variant={statusVariant(selectedQuote.status)} />
              <span className="inline-flex rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
                {quoteWorkflowStage || quoteStatus}
              </span>
            </div>
            <p className="text-sm font-black text-foreground">{selectedQuote.friendly_quote_id}</p>
            <DescriptionText className="text-[10px]">{formatDate(selectedQuote.updated_at)}</DescriptionText>
          </VerticalStack>
          <button
            type="button"
            onClick={() => void navigator.clipboard.writeText(selectedQuote.quote_id)}
            className="flex items-center gap-1.5 text-left group w-full"
            title="Click to copy UUID"
          >
            <Hash className="h-3 w-3 text-muted-foreground/40 shrink-0" />
            <span className="font-mono text-[9px] text-muted-foreground/40 truncate group-hover:text-muted-foreground transition-colors">
              {selectedQuote.quote_id}
            </span>
          </button>
        </VerticalStack>

        <VerticalStack gap={3}>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 px-1">Workflow</p>
          <WorkflowStepper quote={selectedQuote} />
        </VerticalStack>
      </VerticalStack>
    );

    // ── Mobile top band ────────────────────────────────────────────────────
    const mobileTopBand = (
      <div className="lg:hidden shrink-0 border-b border-border/40 bg-muted/5 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2 flex-wrap">
          <RegistryStatus label={formatStatusLabel(selectedQuote.status)} variant={statusVariant(selectedQuote.status)} />
          <span className="inline-flex rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
            {quoteWorkflowStage || quoteStatus}
          </span>
          <span className="ml-auto font-mono text-[10px] font-bold text-foreground/60 truncate max-w-[160px]" title={selectedQuote.friendly_quote_id}>
            {selectedQuote.friendly_quote_id}
          </span>
        </div>
        <div className="mt-2">
          <WorkflowStepper quote={selectedQuote} />
        </div>
      </div>
    );

    return (
      <>
        {/* Header — title + subtitle + close, same as DisplayWorkspace */}
        <div className="shrink-0 border-b border-border/40 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-lg font-black tracking-tight text-foreground">
                {isConstructionRequest ? 'Construction Request' : 'Quote'} {selectedQuote.friendly_quote_id}
              </p>
              <p className="text-sm text-muted-foreground">
                {`${(selectedQuote.customer_snapshot as { first_name?: string })?.first_name || ''} ${(selectedQuote.customer_snapshot as { last_name?: string })?.last_name || ''}`.trim() || 'Unassigned Customer'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCloseQuote}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-all hover:bg-muted"
              aria-label="Close panel"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body: sidebar + main column */}
        <div className="flex flex-1 min-h-0">
          {/* Desktop sidebar */}
          <div className="hidden lg:flex w-56 xl:w-64 shrink-0 flex-col border-r border-border/40 overflow-y-auto p-6">
            {sidebar}
          </div>

          {/* Main column */}
          <div className="flex flex-1 flex-col min-w-0 min-h-0">
            {mobileTopBand}

            {/* Scrollable area with sticky tab bar */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="pb-6">
                {/* Sticky tab bar */}
                <div className="sticky top-0 z-10 flex border-b border-border/40 bg-background">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'flex items-center gap-1.5 px-4 py-3 text-xs font-bold transition-all border-b-2 -mb-px',
                        activeTab === tab.id
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border/60'
                      )}
                    >
                      {tab.label}
                      {tab.count !== undefined && tab.count > 0 && (
                        <span className={cn(
                          'rounded-full px-1.5 py-px text-[9px] font-black tabular-nums',
                          activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        )}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="px-1">
                  {activeTab === 'overview' && overviewContent}
                  {activeTab === 'items' && itemsContent}
                  {activeTab === 'notes' && notesContent}
                  {activeTab === 'activity' && activityContent}
                </div>
              </div>
            </div>

            {/* Action footer */}
            <PaneFooter className="shrink-0 bg-muted/5 border-t border-border px-6 py-4">
              {submittingKey && (
                <div className="flex items-center gap-2 text-[10px] font-bold text-primary mb-3">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Processing...
                </div>
              )}
              {confirmingAction ? (
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-destructive/70 shrink-0" />
                  <span className="text-xs font-bold text-foreground flex-1">
                    {confirmingAction === 'cancel' ? 'Cancel this quote?' : 'Reject this quote?'} This cannot be undone.
                  </span>
                  <button type="button" className={secondaryButtonClass} onClick={() => setConfirmingAction(null)}>Never Mind</button>
                  <button type="button" className={destructiveButtonClass} onClick={() => void handleQuoteAction(confirmingAction as 'cancel' | 'reject')} disabled={submittingKey !== null}>
                    {confirmingAction === 'cancel' ? 'Yes, Cancel' : 'Yes, Reject'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    {((!isConstructionRequest && (quoteStatus === 'draft' || quoteStatus === 'submitted' || quoteStatus === 'approved')) ||
                      (isConstructionRequest && (
                        quoteWorkflowStage === 'requested' ||
                        quoteWorkflowStage === 'revision_requested' ||
                        (isCatalogOwnerWorkspace && quoteWorkflowStage !== 'converted' && quoteWorkflowStage !== 'cancelled')
                      ))) && (
                      <button type="button" className={cn(secondaryButtonClass, 'text-muted-foreground hover:text-destructive hover:border-destructive/40')} onClick={() => setConfirmingAction('cancel')} disabled={submittingKey !== null}>
                        Cancel Quote
                      </button>
                    )}
                    {((!isConstructionRequest && quoteStatus === 'submitted') ||
                      (isConstructionRequest && isCatalogOwnerWorkspace && quoteWorkflowStage === 'submitted_to_customer')) && (
                      <button type="button" className={cn(secondaryButtonClass, 'hover:text-destructive hover:border-destructive/40')} onClick={() => setConfirmingAction('reject')} disabled={submittingKey !== null}>
                        <Ban className="h-3.5 w-3.5" />
                        Reject
                      </button>
                    )}
                  </div>

                  <div className="flex-1" />

                  <div className="flex items-center gap-2">
                    {!isConstructionRequest && quoteStatus === 'draft' && (
                      <button type="button" className={secondaryButtonClass} onClick={() => void handleQuoteAction('submit')} disabled={submittingKey !== null}>
                        <Send className="h-3.5 w-3.5" />
                        Submit Quote
                      </button>
                    )}
                    {!isConstructionRequest && quoteStatus === 'submitted' && (
                      <button type="button" className={cn(primaryButtonClass, 'bg-emerald-600 hover:opacity-90')} onClick={() => void handleQuoteAction('approve')} disabled={submittingKey !== null}>
                        <Check className="h-3.5 w-3.5" />
                        Approve
                      </button>
                    )}
                    {!isConstructionRequest && quoteStatus === 'approved' && (
                      <button type="button" className={primaryButtonClass} onClick={() => void handleConvert()} disabled={submittingKey !== null}>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Convert to Order
                      </button>
                    )}
                    {isConstructionRequest && canSellerRequestRevision && (
                      <button type="button" className={secondaryButtonClass} onClick={() => void handleSellerRevisionRequest()} disabled={submittingKey !== null}>
                        Request Revision
                      </button>
                    )}
                    {isConstructionRequest && isCatalogOwnerWorkspace && quoteWorkflowStage === 'priced' && (
                      <button type="button" className={secondaryButtonClass} onClick={() => void handleQuoteAction('submit')} disabled={submittingKey !== null}>
                        Send to Customer
                      </button>
                    )}
                    {isConstructionRequest && isCatalogOwnerWorkspace && quoteWorkflowStage === 'submitted_to_customer' && (
                      <button type="button" className={cn(primaryButtonClass, 'bg-emerald-600 hover:opacity-90')} onClick={() => void handleOwnerWorkflowTransition('accepted')} disabled={submittingKey !== null}>
                        <Check className="h-3.5 w-3.5" />
                        Mark Accepted
                      </button>
                    )}
                    {isConstructionRequest && isCatalogOwnerWorkspace && quoteWorkflowStage === 'accepted' && (
                      <button type="button" className={primaryButtonClass} onClick={() => void handleConvert()} disabled={submittingKey !== null}>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Convert to Order
                      </button>
                    )}
                  </div>
                </div>
              )}
            </PaneFooter>
          </div>
        </div>
      </>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Quotes' }]}
          title={workspaceTitle}
          description={workspaceDescription}
        />
      }
      takeoverActive={!!selectedQuoteId}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={
        <div className="h-full flex flex-col bg-background border-l border-border shadow-2xl animate-in slide-in-from-right duration-300">
          {/* Auto-dismiss toast */}
          {toastVisible && message && (
            <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg border border-border/60 bg-background px-4 py-3 shadow-lg shadow-black/10 animate-in slide-in-from-bottom-2 duration-200">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
              <p className="text-sm font-bold text-foreground">{message}</p>
            </div>
          )}
          {renderDetailPanel()}
        </div>
      }
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={quotes}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={(updater) => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const statusFilter = next.find(entry => entry.id === 'status');
              query.setFacet('status', Array.isArray(statusFilter?.value) ? statusFilter.value.map(String) : []);
            }}
            globalFilter={query.search}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination={true}
            totalRowCount={totalQuotes}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination={true}
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">No Quotes Found</p>
                <DescriptionText className="mt-1 text-xs">
                  {query.search || selectedStatuses.length > 0
                    ? 'No quotes match the current search or filters.'
                    : workspace === 'catalog-owner'
                      ? 'Requested and priced quotes for the active catalog owner will appear here.'
                      : 'Seller-submitted quote requests for the active tenant will appear here.'}
                </DescriptionText>
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar
                table={table}
                searchValue={query.search}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all quotes..."
                resultCount={totalQuotes}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  table.getColumn('status') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set((table.getColumn('status')?.getFilterValue() as string[]) ?? [])}
                      onFilterChange={values => table.getColumn('status')?.setFilterValue(Array.from(values))}
                      options={ALL_QUOTE_STATUS_OPTIONS.map(option => ({
                        label: statusFacets.find(bucket => bucket.value === option)?.label || formatStatusLabel(option),
                        value: option,
                      }))}
                      counts={Object.fromEntries(statusFacets.map(bucket => [bucket.value, bucket.count]))}
                    />
                  )
                }
                bulkActions={
                  <button type="button" className={secondaryButtonClass} onClick={() => void loadQuoteList()} disabled={loading || detailLoading}>
                    {loading ? 'Refreshing...' : 'Refresh Queue'}
                  </button>
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
